import React, { useMemo, useRef, useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from 'components/common/PageHeader';
import { Editor } from '@tinymce/tinymce-react';
import { createBlogPost, fetchBlogPost, updateBlogPost, uploadBlogImage } from 'helpers/blog';

const EMPTY_FORM = {
  title: '',
  subtitle: '',
  category: '',
  author_name: '',
  excerpt: '',
  content: '',
  cover_image_url: '',
  accent_color: '#0b5ed7',
  reading_time: '',
  meta_title: '',
  meta_description: '',
  faq_mode: 'manual',
  faq_manual: '',
  faq_json: '',
  extra_meta: '',
  is_featured: false,
  is_published: false
};

const BlogEditorPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { postId } = useParams();
  const isEditing = Boolean(postId);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const coverInputRef = useRef(null);

  const tinyMceInit = useMemo(
    () => ({
      height: '60vh',
      min_height: 420,
      autoresize_min_height: 420,
      menubar: 'file edit view insert format tools table help',
      statusbar: true,
      branding: false,
      plugins: [
        'advlist',
        'autolink',
        'lists',
        'link',
        'image',
        'charmap',
        'preview',
        'anchor',
        'searchreplace',
        'visualblocks',
        'code',
        'fullscreen',
        'insertdatetime',
        'media',
        'table',
        'help',
        'wordcount',
        'emoticons',
        'codesample',
        'quickbars',
        'autoresize',
        'hr',
        'directionality',
        'nonbreaking',
        'pagebreak',
        'save',
        'template',
        'toc',
        'visualchars'
      ],
      toolbar:
        'undo redo | blocks fontsize | bold italic underline strikethrough | forecolor backcolor | ' +
        'alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | ' +
        'link image media table codesample blockquote hr | charmap emoticons pagebreak nonbreaking | ' +
        'searchreplace visualblocks visualchars code fullscreen preview | removeformat',
      toolbar_mode: 'sliding',
      quickbars_selection_toolbar: 'bold italic | quicklink h2 h3 blockquote quickimage quicktable',
      contextmenu: 'link image table',
      image_title: true,
      automatic_uploads: true,
      file_picker_types: 'image',
      paste_data_images: true,
      convert_urls: false,
      images_upload_handler: blobInfo => {
        return uploadBlogImage(blobInfo.blob())
          .then(response => response.location || response.url || '')
          .catch(error => {
            const message = error?.response?.data?.error || t('BLOG.IMAGE_UPLOAD_ERROR');
            return Promise.reject(message);
          });
      },
      content_style: 'body { font-family: Helvetica, Arial, sans-serif; font-size: 15px; }'
    }),
    [t]
  );

  useEffect(() => {
    if (!isEditing) {
      return;
    }

    const loadPost = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await fetchBlogPost(postId);
        setForm({
          ...EMPTY_FORM,
          ...response.post
        });
      } catch (requestError) {
        setError(requestError.response?.data?.error || t('BLOG.ERROR_LOAD'));
      } finally {
        setLoading(false);
      }
    };

    loadPost();
  }, [isEditing, postId, t]);

  const handleChange = event => {
    const { name, value, type, checked } = event.target;
    setForm(current => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async event => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = { ...form };
      const response = isEditing
        ? await updateBlogPost(postId, payload)
        : await createBlogPost(payload);
      toast.success(t(isEditing ? 'BLOG.TOAST_UPDATED' : 'BLOG.TOAST_CREATED'), { theme: 'colored' });
      navigate(`/workspace/blog/${response.post.id}`);
    } catch (requestError) {
      setError(requestError.response?.data?.error || t('BLOG.ERROR_SAVE'));
    } finally {
      setSaving(false);
    }
  };

  const handleUploadCoverImage = async event => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setUploadingCover(true);
    try {
      const response = await uploadBlogImage(file);
      setForm(current => ({
        ...current,
        cover_image_url: response.url || response.location || ''
      }));
      toast.success(t('BLOG.IMAGE_UPLOAD_SUCCESS'), { theme: 'colored' });
    } catch (requestError) {
      toast.error(requestError.response?.data?.error || t('BLOG.IMAGE_UPLOAD_ERROR'), { theme: 'colored' });
    } finally {
      setUploadingCover(false);
      if (coverInputRef.current) {
        coverInputRef.current.value = '';
      }
    }
  };

  if (loading) {
    return <div className="d-flex justify-content-center py-6"><Spinner animation="border" /></div>;
  }

  return (
    <>
      <PageHeader
        title={t(isEditing ? 'BLOG.EDIT_TITLE' : 'BLOG.CREATE_TITLE')}
        description={t('BLOG.EDITOR_DESCRIPTION')}
        className="mb-4"
      />

      {error ? <Alert variant="danger">{error}</Alert> : null}

      <Form onSubmit={handleSubmit}>
        <Row className="g-4">
          <Col lg={8}>
            <Card className="border">
              <Card.Header>
                <h5 className="mb-0">{t('BLOG.CONTENT_SECTION')}</h5>
              </Card.Header>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>{t('BLOG.FIELDS.TITLE')}</Form.Label>
                  <Form.Control name="title" value={form.title} onChange={handleChange} required />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>{t('BLOG.FIELDS.SUBTITLE')}</Form.Label>
                  <Form.Control name="subtitle" value={form.subtitle} onChange={handleChange} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>{t('BLOG.FIELDS.EXCERPT')}</Form.Label>
                  <Form.Control as="textarea" rows={3} name="excerpt" value={form.excerpt} onChange={handleChange} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>{t('BLOG.FIELDS.CONTENT')}</Form.Label>
                  <Editor
                    apiKey={process.env.REACT_APP_TINYMCE_APIKEY}
                    value={form.content}
                    onEditorChange={value => setForm(current => ({ ...current, content: value }))}
                    init={tinyMceInit}
                  />
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={4}>
            <Card className="border mb-4">
              <Card.Header>
                <h5 className="mb-0">{t('BLOG.SETTINGS_SECTION')}</h5>
              </Card.Header>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>{t('BLOG.FIELDS.CATEGORY')}</Form.Label>
                  <Form.Control name="category" value={form.category} onChange={handleChange} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>{t('BLOG.FIELDS.AUTHOR_NAME')}</Form.Label>
                  <Form.Control name="author_name" value={form.author_name} onChange={handleChange} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>{t('BLOG.FIELDS.READING_TIME')}</Form.Label>
                  <Form.Control name="reading_time" value={form.reading_time} onChange={handleChange} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>{t('BLOG.FIELDS.COVER_IMAGE_URL')}</Form.Label>
                  <Form.Control name="cover_image_url" value={form.cover_image_url} onChange={handleChange} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>{t('BLOG.FIELDS.UPLOAD_PORTRAIT')}</Form.Label>
                  <Form.Control ref={coverInputRef} type="file" accept="image/*" onChange={handleUploadCoverImage} disabled={uploadingCover} />
                  <Form.Text className="text-muted">{uploadingCover ? t('BLOG.UPLOADING_IMAGE') : t('BLOG.PORTRAIT_HELP')}</Form.Text>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>{t('BLOG.FIELDS.ACCENT_COLOR')}</Form.Label>
                  <Form.Control type="color" name="accent_color" value={form.accent_color} onChange={handleChange} />
                </Form.Group>
                <Form.Check className="mb-3" type="checkbox" name="is_featured" label={t('BLOG.FIELDS.IS_FEATURED')} checked={form.is_featured} onChange={handleChange} />
                <Form.Check className="mb-0" type="checkbox" name="is_published" label={t('BLOG.FIELDS.IS_PUBLISHED')} checked={form.is_published} onChange={handleChange} />
              </Card.Body>
            </Card>

            <Card className="border mb-4">
              <Card.Header>
                <h5 className="mb-0">{t('BLOG.SEO_SECTION')}</h5>
              </Card.Header>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>{t('BLOG.FIELDS.META_TITLE')}</Form.Label>
                  <Form.Control name="meta_title" value={form.meta_title} onChange={handleChange} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>{t('BLOG.FIELDS.META_DESCRIPTION')}</Form.Label>
                  <Form.Control as="textarea" rows={4} name="meta_description" value={form.meta_description} onChange={handleChange} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>{t('BLOG.FIELDS.EXTRA_META')}</Form.Label>
                  <Form.Control as="textarea" rows={6} name="extra_meta" value={form.extra_meta} onChange={handleChange} placeholder={t('BLOG.FIELDS.EXTRA_META_PLACEHOLDER')} />
                </Form.Group>
              </Card.Body>
            </Card>

            <Card className="border mb-4">
              <Card.Header>
                <h5 className="mb-0">{t('BLOG.FAQ_SECTION')}</h5>
              </Card.Header>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>{t('BLOG.FIELDS.FAQ_MODE')}</Form.Label>
                  <Form.Select name="faq_mode" value={form.faq_mode} onChange={handleChange}>
                    <option value="manual">{t('BLOG.FIELDS.FAQ_MODE_MANUAL')}</option>
                    <option value="json">{t('BLOG.FIELDS.FAQ_MODE_JSON')}</option>
                  </Form.Select>
                </Form.Group>
                {form.faq_mode === 'manual' ? (
                  <Form.Group className="mb-0">
                    <Form.Label>{t('BLOG.FIELDS.FAQ_MANUAL')}</Form.Label>
                    <Form.Control as="textarea" rows={8} name="faq_manual" value={form.faq_manual} onChange={handleChange} placeholder={t('BLOG.FIELDS.FAQ_MANUAL_PLACEHOLDER')} />
                  </Form.Group>
                ) : (
                  <Form.Group className="mb-0">
                    <Form.Label>{t('BLOG.FIELDS.FAQ_JSON')}</Form.Label>
                    <Form.Control as="textarea" rows={8} name="faq_json" value={form.faq_json} onChange={handleChange} placeholder={t('BLOG.FIELDS.FAQ_JSON_PLACEHOLDER')} />
                  </Form.Group>
                )}
              </Card.Body>
            </Card>

            <div className="d-flex gap-2">
              <Button type="submit" disabled={saving || !form.title.trim()}>
                {saving ? t('BLOG.SAVING') : t('BLOG.SAVE')}
              </Button>
              <Button variant="outline-secondary" onClick={() => navigate('/workspace/blog')}>
                {t('BLOG.BACK_TO_LIST')}
              </Button>
            </div>
          </Col>
        </Row>
      </Form>
    </>
  );
};

export default BlogEditorPage;
